import { FC, PropsWithChildren, useMemo, useState } from 'react';
import React from 'react';
import { useContext } from 'react';

import { BoundingBox } from '../components/ContextMenu';
import { DagContextMenu, DagContextMenuOption } from '../components/DagContextMenu';
import { InteractionDagNode } from '../dag/internalDag';
import { DbtDagNode } from '../dbt-dag';
import { useDagContext } from './DagProvider';

type DagContextMenuComponentType = FC<{
  dagContainerEl: HTMLDivElement | null | undefined;
  boundingBox: BoundingBox;
}>;

interface DagContextMenuContextType<TDbtDagNode extends DbtDagNode> {
  hideContextMenu: () => void;
  onNodeContextMenu: (
    event: React.MouseEvent,
    node: InteractionDagNode<TDbtDagNode>,
  ) => void;
  DagContextMenu: DagContextMenuComponentType;
}

export const DagContextMenuContext = React.createContext<
  DagContextMenuContextType<any>
>({
  hideContextMenu: () => undefined,
  onNodeContextMenu: () => undefined,
  DagContextMenu: () => null,
});

export const useContextMenuProvider = () => {
  return useContext(DagContextMenuContext);
};

interface DagContextMenuProviderProps<TDbtDagNode extends DbtDagNode> {
  /**
   * Returns a list of ContextMenu options. If not defined, there will
   * be no ContextMenu (menu that opens on right-click).
   */
  getContextMenuOptions?: (
    node: InteractionDagNode<TDbtDagNode>,
  ) => DagContextMenuOption[];
}

export const DagContextMenuProvider = <TDbtDagNode extends DbtDagNode>({
  children,
  getContextMenuOptions,
}: PropsWithChildren<DagContextMenuProviderProps<TDbtDagNode>>) => {
  // If greater than 1, this represent a list of items to be rendered in a right-click Context menu
  const [dagContextMenuOptions, setDagContextMenuOptions] = useState<
    DagContextMenuOption[]
  >([]);

  // The coordinates of any Context Menu that will be rendered
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: -Infinity,
    y: -Infinity,
  });
  // The last time a context menu was requested, forces repositioning/re-rendering
  const [contextMenuTimestamp, setContextMenuTimestamp] = useState(0);

  const dagContext = useDagContext();
  const value = useMemo(() => {
    const DagContextMenuWrapper: DagContextMenuComponentType = ({
      dagContainerEl,
      boundingBox,
    }) => {
      if (!dagContextMenuOptions.length) return null;
      return (
        <DagContextMenu
          rootElement={dagContainerEl}
          options={dagContextMenuOptions}
          position={contextMenuPosition}
          timestamp={contextMenuTimestamp}
          boundingBox={boundingBox}
        />
      );
    };

    return {
      DagContextMenu: DagContextMenuWrapper,
      hideContextMenu: () => {
        setDagContextMenuOptions([]);
      },
      onNodeContextMenu: (
        event: React.MouseEvent,
        node: InteractionDagNode<TDbtDagNode>,
      ) => {
        event.preventDefault();
        dagContext.updateHighlightedNode([node.id]);
        dagContext.onNodeRightClick(event, {
          ...node,
          isSelected: false,
          isHidden: false,
        });
        if (!getContextMenuOptions) return;
        const dagContextMenuOptions = getContextMenuOptions(node);
        if (dagContextMenuOptions && dagContextMenuOptions.length > 0) {
          setDagContextMenuOptions(dagContextMenuOptions);
          setContextMenuPosition({ x: event.pageX, y: event.pageY });
          setContextMenuTimestamp(Date.now());
        }
      },
    };
  }, [
    contextMenuPosition,
    contextMenuTimestamp,
    dagContext,
    dagContextMenuOptions,
    getContextMenuOptions,
  ]);

  return (
    <DagContextMenuContext.Provider value={value}>
      {children}
    </DagContextMenuContext.Provider>
  );
};
